import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FaHeart, FaRegHeart, FaStar, FaWineBottle, FaShoppingCart, FaArrowLeft, FaBalanceScale, FaTrash, FaTimes, FaChevronDown, FaChevronUp, FaShare, FaCopy, FaCheck, FaRobot, FaLightbulb, FaFacebookF, FaWhatsapp, FaEnvelope, FaSms, FaEllipsisH, FaShareAlt, FaLock, FaLink, FaCheckCircle } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toSentenceCase } from '../utils/textUtils';
import { getUserCountry, getUserLanguage, detectUserLocation } from '../utils/geolocation';
import { useTranslation } from 'react-i18next';
import AIChatPopup from '../components/AIChatPopup';
import AITooltip from '../components/AITooltip';
import RichText from '../components/RichText';
import { hasWelcomeAccessTo } from '../utils/welcomeAccess';
import { trackEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { hasPremiumAccess } from '../config/subscriptions';
import { getErrorMessage } from '../utils/errorMessage';

const RecipeDetailPage = ({ sessionId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scaledData, setScaledData] = useState(null);
  // Track which recipe IDs we've already fired `recipe_open` for so
  // subsequent refetches (rating, favorite, comment refresh) don't
  // double-count the same page-view in GA4.
  const recipeOpenFiredRef = React.useRef(new Set());
  const [targetVolume, setTargetVolume] = useState(null);
  const [targetVolumeManuallyEdited, setTargetVolumeManuallyEdited] = useState(false);
  const [fullMachineVolume, setFullMachineVolume] = useState(12000);
  const [hasDefaultMachine, setHasDefaultMachine] = useState(false);
  const [useHalfPortion, setUseHalfPortion] = useState(false);
  const [machines, setMachines] = useState([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [products, setProducts] = useState([]);
  const [userCountry, setUserCountry] = useState('DK');
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [commentsCollapsed, setCommentsCollapsed] = useState(true); // Start collapsed
  
  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // AI state
  const [showTipsAIPopup, setShowTipsAIPopup] = useState(false);
  const [showTipsTooltip, setShowTipsTooltip] = useState(true);

  // Admin: Match-token debug
  const [matchIndex, setMatchIndex] = useState(null);
  const [matchIndexOpen, setMatchIndexOpen] = useState(false);
  const [matchIndexLoading, setMatchIndexLoading] = useState(false);

  const fetchMatchIndex = async () => {
    if (matchIndex) return; // cache
    setMatchIndexLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/recipes/${id}/match-tokens`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMatchIndex(response.data);
    } catch (error) {
      console.error('Error fetching match-tokens:', error);
      toast.error('Kunne ikke hente match-index');
    } finally {
      setMatchIndexLoading(false);
    }
  };

  const reindexRecipe = async () => {
    setMatchIndexLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/recipes/${id}/reindex`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMatchIndex(null);
      const response = await axios.get(`${API}/admin/recipes/${id}/match-tokens`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMatchIndex(response.data);
      toast.success('Match-index genberegnet');
    } catch (error) {
      console.error('Error reindexing recipe:', error);
      toast.error('Kunne ikke genberegne match-index');
    } finally {
      setMatchIndexLoading(false);
    }
  };
  
  // Get localized ingredient name
  const getLocalizedIngredientName = (ingredient) => {
    try {
      const userLang = getUserLanguage();
      
      // Log every call for debugging
      console.log('[INGREDIENT]', ingredient.name, 'has name_fr?', ingredient.name_fr, 'userLang:', userLang);
      
      const langMap = {
        'da': 'name_da',
        'dk': 'name_da',
        'fr': 'name_fr',
        'de': 'name_de',
        'en': 'name_en',
        'en-gb': 'name_en',
        'en-us': 'name_en'
      };
      
      const fieldName = langMap[userLang.toLowerCase()] || 'name_da';
      const localizedName = ingredient[fieldName];
      
      // Return localized name if it exists and is different from original, otherwise fall back to default name
      if (localizedName && localizedName !== ingredient.name) {
        console.log('[INGREDIENT] Using:', localizedName);
        return localizedName;
      }
      
      console.log('[INGREDIENT] Fallback to:', ingredient.name);
      return ingredient.name;
    } catch (error) {
      console.error('[INGREDIENT ERROR]', error);
      return ingredient.name;
    }
  };

  // Category display name mapper
  const getCategoryDisplayName = (categoryKey) => {
    const categoryMap = {
      'vand': 'Vand',
      'mælk': 'Mælk',
      'maelk': 'Mælk',
      'frugtsaft': 'Frugtsaft',
      'laeskedrik': 'Læskedrik',
      'kaffe': 'Kaffe',
      'te': 'Te',
      'smoothie': 'Smoothie',
      'andetdrink': 'Andet drink'
    };
    return categoryMap[categoryKey] || categoryKey;
  };

  useEffect(() => {
    fetchRecipe();
    fetchMachines();
    fetchProducts();
    if (isAdmin()) {
      fetchAllRecipesForNavigation();
    }
    // Detect user country for product links via backend API (IP-based)
    detectUserLocation().then(result => {
      setUserCountry(result.country_code);
      console.log('[RecipeDetail] User country detected:', result.country_code, 'Source:', result.source);
    }).catch(error => {
      console.error('[RecipeDetail] Country detection failed:', error);
      setUserCountry('DK'); // Fallback to Denmark
    });
  }, [id, sessionId, user]);

  const fetchAllRecipesForNavigation = async () => {
    try {
      const response = await axios.get(`${API}/recipes?session_id=${sessionId}&alcohol=both&lang=${getUserLanguage()}`);
      // Sort alphabetically for admin navigation
      const sorted = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'da')
      );
      setAllRecipes(sorted);
      
      console.log(`[Admin Nav] Total recipes: ${sorted.length}, Current ID: ${id}`);
      
      // Find current recipe index
      const currentIndex = sorted.findIndex(r => r.id === id);
      console.log(`[Admin Nav] Current index: ${currentIndex}`);
      
      if (currentIndex !== -1) {
        // Set next recipe
        if (currentIndex < sorted.length - 1) {
          const nextId = sorted[currentIndex + 1].id;
          setNextRecipeId(nextId);
          console.log(`[Admin Nav] Next recipe ID: ${nextId}, Name: ${sorted[currentIndex + 1].name}`);
        } else {
          setNextRecipeId(null); // Last recipe
          console.log(`[Admin Nav] Last recipe, no next`);
        }
        
        // Set previous recipe
        if (currentIndex > 0) {
          const prevId = sorted[currentIndex - 1].id;
          setPrevRecipeId(prevId);
          console.log(`[Admin Nav] Prev recipe ID: ${prevId}, Name: ${sorted[currentIndex - 1].name}`);
        } else {
          setPrevRecipeId(null); // First recipe
          console.log(`[Admin Nav] First recipe, no prev`);
        }
      } else {
        console.log(`[Admin Nav] Current recipe not found in list!`);
      }
    } catch (error) {
      console.error('Error fetching all recipes:', error);
    }
  };

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}?session_id=${sessionId}&lang=${getUserLanguage()}`);
      setRecipe(response.data);
      // Init skaleringsfelt til opskriftens egen basisvolumen — så delte
      // links (Facebook/Google) altid åbner med korrekt volumen + originale
      // ingredienser. Maskinens tankvolumen må IKKE overskrive dette.
      // Fallback = 2700 ml (matcher line ~976 hvor samme fallback bruges).
      setTargetVolume(response.data.base_volume_ml || 2700);
      // Ny opskrift åbnet — nulstil "manuelt redigeret"-flaget så den
      // efterfølgende maskine-volumen-forudfyldning (useEffect nedenfor)
      // kan slå til igen for DENNE opskrift.
      setTargetVolumeManuallyEdited(false);
      if (response.data.user_rating) {
        setRating(response.data.user_rating);
      }
      // recipe_open analytics — fire ONCE per recipe per mount lifetime.
      // The page refetches on rating/favorite/comment changes, and we don't
      // want each of those to count as a fresh page-view in GA4.
      if (!recipeOpenFiredRef.current.has(response.data.id)) {
        recipeOpenFiredRef.current.add(response.data.id);
        trackEvent(ANALYTICS_EVENTS.RECIPE_OPEN, {
          recipe_id: response.data.id,
          recipe_name: response.data.name,
          recipe_type: response.data.type,
          recipe_family: response.data.family || null,
          contains_alcohol: !!response.data.alcohol_flag,
          is_free: !!response.data.is_free,
          author: response.data.author === 'system' ? 'system' : 'community',
        });
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
      toast.error('Kunne ikke hente opskrift');
    } finally {
      setLoading(false);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machines/${sessionId}`);
      setMachines(response.data);
      if (response.data.length > 0) {
        const defaultMachine = response.data.find(m => m.is_default) || response.data[0];
        const machineVolume = defaultMachine.tank_volumes_ml[0];
        // Behold maskinvolumen til half-portion-checkboxen og 6L/12L-referencer,
        // men lad IKKE den overskrive targetVolume — brugeren skal altid åbne
        // opskriften med dens egen basisvolumen (per user 04-Feb-2026).
        setFullMachineVolume(machineVolume);
        setHasDefaultMachine(true);
        // Return true to indicate a machine was found
        return true;
      }
      setHasDefaultMachine(false);
      return false;
    } catch (error) {
      console.error('Error fetching machines:', error);
      return false;
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Comment functions
  const fetchComments = async () => {
    try {
      // Fetch comments, optionally filtered by user's language
      const params = user?.country ? `?language=${getLanguageFromCountry(user.country)}` : '';
      const response = await axios.get(`${API}/comments/${id}${params}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const getLanguageFromCountry = (country) => {
    const countryToLang = {
      'DK': 'da',
      'DE': 'de',
      'FR': 'fr',
      'GB': 'en',
      'US': 'en-US'
    };
    return countryToLang[country] || 'da';
  };

  const getLanguageFlag = (lang) => {
    const flags = {
      'da': '🇩🇰',
      'de': '🇩🇪',
      'fr': '🇫🇷',
      'en': '🇬🇧',
      'en-US': '🇺🇸'
    };
    return flags[lang] || '🌍';
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Kommentaren kan ikke være tom');
      return;
    }

    try {
      const response = await axios.post(`${API}/comments`, {
        recipe_id: id,
        comment: newComment
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Kommentar tilføjet');
    } catch (error) {
      console.error('Error adding comment:', error);
      if (error.response?.status === 403) {
        toast.error('Kun medlemmer (Light + Pro) kan kommentere. Opgradér for at deltage i diskussionerne!');
      } else {
        toast.error('Kunne ikke tilføje kommentar');
      }
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) {
      toast.error('Kommentaren kan ikke være tom');
      return;
    }

    try {
      const response = await axios.put(`${API}/comments/${commentId}`, {
        comment: editCommentText
      });
      setComments(comments.map(c => c.id === commentId ? response.data : c));
      setEditingCommentId(null);
      setEditCommentText('');
      toast.success('Kommentar opdateret');
    } catch (error) {
      console.error('Error editing comment:', error);
      toast.error('Kunne ikke opdatere kommentar');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(t('recipeDetail.confirmDeleteComment', 'Er du sikker på, at du vil slette denne kommentar?'))) {
      return;
    }

    try {
      await axios.delete(`${API}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success(t('recipeDetail.commentDeleted', 'Kommentar slettet'));
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error(t('recipeDetail.deleteCommentError', 'Kunne ikke slette kommentar'));
    }
  };

  const handleToggleLike = async (commentId) => {
    try {
      const response = await axios.post(`${API}/comments/${commentId}/like`);
      // Update likes count in UI
      setComments(comments.map(c => {
        if (c.id === commentId) {
          const isLiked = c.liked_by?.includes(user?.id);
          return {
            ...c,
            likes: response.data.likes,
            liked_by: isLiked 
              ? c.liked_by.filter(uid => uid !== user.id)
              : [...(c.liked_by || []), user.id]
          };
        }
        return c;
      }));
    } catch (error) {
      console.error('Error toggling like:', error);
      if (error.response?.status === 403) {
        toast.error(t('recipeDetail.proOnlyLikes', 'Kun medlemmer (Light + Pro) kan like kommentarer'));
      } else {
        toast.error('Kunne ikke like kommentar');
      }
    }
  };

  const [allMappings, setAllMappings] = useState([]);
  const [supplierCache, setSupplierCache] = useState({});
  const [allRecipes, setAllRecipes] = useState([]);
  const [nextRecipeId, setNextRecipeId] = useState(null);
  const [prevRecipeId, setPrevRecipeId] = useState(null);
  
  const REDIRECT_API = `${API}/go`;

  useEffect(() => {
    fetchRecipe();
    fetchMachines();
    fetchMappings();
    fetchComments();
  }, [id, sessionId]);

  // Loads the public product-link mappings (id + name + keywords) used
  // to render "Indkøb" buttons next to ingredients for every user.
  // This is separate from the Match Finder's match_tokens system.
  const fetchMappings = async () => {
    try {
      const response = await axios.get(`${API}/mappings`);
      setAllMappings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching product-link mappings:', error);
    }
  };

  const getMappingForIngredient = (ingredientName) => {
    const name = ingredientName.toLowerCase().trim();
    const ingredientWords = name.split(/\s+/); // Split ingredient name into words
    
    let bestMatch = null;
    let bestMatchScore = 0;
    
    // Check against all mappings' keywords
    for (const mapping of allMappings) {
      if (mapping.keywords) {
        const allKeywords = mapping.keywords.toLowerCase().split(/[,;]/).map(k => k.trim()).filter(k => k);
        
        // First check: Exact match (highest priority)
        if (allKeywords.includes(name)) {
          return mapping.id; // Exact match - return immediately
        }
        
        // Second check: Match only if ingredient has SAME number of words as keyword
        // AND each word matches exactly (not as substring)
        for (const keyword of allKeywords) {
          const keywordWords = keyword.split(/\s+/);
          
          // Only match if word count is the same
          if (keywordWords.length !== ingredientWords.length) {
            continue;
          }
          
          // All ingredient words must match exactly (as complete words, not substrings)
          // This prevents "vand" from matching "vandmelon"
          const allWordsMatch = ingredientWords.every(ingredientWord => 
            keywordWords.some(keywordWord => keywordWord === ingredientWord)
          );
          
          if (allWordsMatch) {
            // Calculate match score based on keyword length (more specific = better)
            const score = keyword.length;
            
            if (score > bestMatchScore) {
              bestMatch = mapping.id;
              bestMatchScore = score;
            }
          }
        }
      }
    }
    
    return bestMatch;
  };

  const getSupplierDisplayName = (supplier) => {
    const names = {
      'power': 'Power',
      'barshopen': 'Barshopen',
      'bilka': 'Bilka',
      'foetex': 'Føtex',
      'matas': 'Matas',
      'nemlig': 'Nemlig.com',
      'amazon': 'Amazon',
      'other': 'Leverandør'
    };
    return names[supplier] || supplier.charAt(0).toUpperCase() + supplier.slice(1);
  };

  // Renamed semantically: returns true if the *current user* may edit this
  // recipe. We keep the old name as an alias below for legacy callers.
  const canEditRecipe = () => {
    if (!recipe || !user) return false;

    // Admin can edit every recipe (including system ones).
    if (user.role === 'admin') return true;

    // Editor can edit every NON-system recipe (matches backend auth rule
    // `user.role == "editor" and recipe.author != "system"`).
    if (user.role === 'editor' && recipe.author !== 'system') return true;

    // Everyone else can only edit their own recipes.
    return (
      recipe.author === user.id ||
      recipe.author === user.email ||
      recipe.author === sessionId
    );
  };

  // Legacy alias — keep until all call-sites are renamed.
  const isAuthor = canEditRecipe;

  const getProductForIngredient = (categoryKey) => {
    return products.find(p => p.category_key === categoryKey);
  };

  const trackClick = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const scaleRecipe = async (showToast = true) => {
    try {
      const response = await axios.post(`${API}/scale`, {
        recipe_id: id,
        target_volume_ml: targetVolume,
        margin_pct: 5,
        lang: getUserLanguage()
      });
      setScaledData(response.data);
      if (showToast) {
        toast.success('Opskrift skaleret!');
      }
      // recipe_scaled — only the explicit user-click path (auto-scale on
      // mount doesn't fire so we don't pollute the funnel with default
      // machine-scaling events the user didn't choose).
      trackEvent(ANALYTICS_EVENTS.RECIPE_SCALED, {
        recipe_id: id,
        recipe_type: recipe?.type || null,
        target_volume_ml: targetVolume,
        base_volume_ml: recipe?.base_volume_ml || null,
        scale_factor: recipe?.base_volume_ml
          ? Math.round((targetVolume / recipe.base_volume_ml) * 100) / 100
          : null,
      });
    } catch (error) {
      console.error('Error scaling recipe:', error);
      if (showToast) {
        toast.error('Kunne ikke skalere opskrift');
      }
    }
  };

  // Skalering skal KUN ske når brugeren aktivt ændrer volumen (6L / 12L /
  // halv portion / manuel indtastning + Skalér). Ved første åbning — inkl.
  // delte links fra Facebook/Google — vises opskriftens originale ingredienser
  // ved basisvolumen. Maskinens tankvolumen overskriver aldrig længere
  // targetVolume (per user 04-Feb-2026).
  useEffect(() => {
    if (!recipe || targetVolume == null) return;
    const baseVolume = recipe.base_volume_ml || 2700;
    if (targetVolume === baseVolume) {
      // Ved basisvolumen viser vi opskriftens originale ingredienser direkte.
      setScaledData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.post(`${API}/scale`, {
          recipe_id: id,
          target_volume_ml: targetVolume,
          margin_pct: 5,
          lang: getUserLanguage()
        });
        if (cancelled) return;
        setScaledData(response.data);
        console.log('[Auto-Scale] Recipe scaled to:', targetVolume, 'ml');
      } catch (error) {
        if (cancelled) return;
        console.error('[Auto-Scale] Error:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [recipe, targetVolume, id]);

  // Forudfyld Mål Volumen med brugerens EGEN standardmaskine — hentet fra
  // "Mine Maskiner" (Indstillinger), samme datakilde som halv-portion og
  // ✓ Standard-badgen. KUN for indloggede brugere, KUN én gang pr.
  // opskrift-visning, og ALDRIG hvis brugeren allerede har rørt feltet
  // manuelt. Opskriftens egen base_volume_ml ændres IKKE i databasen —
  // dette er en rent klient-side default for skaleringsfeltet. Gæster og
  // brugere uden en standardmaskine forbliver ved opskriftens basisvolumen
  // (uændret adfærd, se fetchRecipe).
  useEffect(() => {
    if (!recipe || targetVolumeManuallyEdited) return;
    if (!user || !hasDefaultMachine) return;
    const baseVolume = recipe.base_volume_ml || 2700;
    if (fullMachineVolume > 0 && fullMachineVolume !== baseVolume) {
      setTargetVolume(fullMachineVolume);
    }
  }, [recipe, user, hasDefaultMachine, fullMachineVolume, targetVolumeManuallyEdited]);

  const toggleFavorite = async () => {
    // Check if user is guest
    if (!hasPremiumAccess(user)) {
      toast.error('Kun medlemmer (Light + Pro) kan gemme favoritter. Vælg et abonnement for ubegrænsede funktioner!', {
        duration: 4000
      });
      return;
    }
    
    try {
      if (recipe.is_favorite) {
        await axios.delete(`${API}/favorites/${sessionId}/${id}`);
        toast.success('Fjernet fra favoritter');
      } else {
        await axios.post(`${API}/favorites?session_id=${sessionId}&recipe_id=${id}`);
        toast.success('Tilføjet til favoritter');
      }
      setRecipe({ ...recipe, is_favorite: !recipe.is_favorite });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Kunne ikke opdatere favorit');
    }
  };

  // Share recipe — open to ALL users (incl. guests) on ALL recipes (incl. system).
  // Uses navigator.share() on mobile/tablet for the native share sheet, and
  // falls back to a modal with social buttons + copy-link on desktop.
  // The shared URL points to /api/share/r/{id}, which serves OG metadata for
  // rich social previews and immediately redirects humans to /recipes/{id}.
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/api/share/r/${id}`;
    const shareTitle = recipe?.name ? `${recipe.name} · SlushBook` : 'Slushice-opskrift fra SlushBook';
    const shareText = recipe?.description
      ? recipe.description.replace(/<[^>]+>/g, '').slice(0, 140)
      : 'Tjek denne slushice-opskrift på SlushBook';
    setShareLink(shareUrl);

    // Prefer the native share sheet when available (mobile / iPadOS / some desktops)
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      } catch (err) {
        // AbortError = user dismissed the sheet → just stop silently.
        if (err && err.name === 'AbortError') return;
        // Any other failure → fall through to the modal.
        console.warn('navigator.share failed, falling back to modal:', err);
      }
    }
    setShowShareModal(true);
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      toast.success('Link kopieret!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard API blocked (insecure context, permissions, headless) —
      // surface a graceful fallback so the user can still copy manually.
      toast.error('Kunne ikke kopiere automatisk — vælg linket og kopiér manuelt.');
    }
  };

  // Pre-built social-share URLs reused by the fallback modal buttons.
  // We only include channels with STABLE web share URLs:
  // - Facebook: sharer.php works reliably with just a URL.
  // - WhatsApp: wa.me is the official web entry point.
  // - X/Twitter: intent/tweet is stable.
  // - Email/SMS: native mailto:/sms: schemes — always work.
  // - Copy link + Flere (native share) cover everything else (Messenger,
  //   Instagram, Threads, etc.) on platforms that actually support them.
  // Messenger (`fb-messenger://share/?link=...`) was removed because it
  // hangs for >60s on desktop browsers without the app installed.
  const buildSocialShareUrls = () => {
    const url = shareLink;
    const title = recipe?.name ? `${recipe.name} · SlushBook` : 'Slushice-opskrift';
    const text = recipe?.description
      ? recipe.description.replace(/<[^>]+>/g, '').slice(0, 140)
      : 'Tjek denne slushice-opskrift på SlushBook';
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
      sms: `sms:?&body=${encodeURIComponent(`${title} ${url}`)}`,
    };
  };

  const deleteRecipe = async () => {
    if (!window.confirm(t('recipeDetail.confirmDelete', `Er du sikker på, at du vil slette "{{name}}"? Dette kan ikke fortrydes.`, { name: recipe.name }))) {
      return;
    }

    try {
      await axios.delete(`${API}/recipes/${id}`, {
        withCredentials: true
      });
      toast.success(t('recipeDetail.recipeDeleted', 'Opskrift slettet'));
      navigate('/recipes');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error(getErrorMessage(error, t('recipeDetail.deleteError', 'Kunne ikke slette opskrift')));
    }
  };

  const rateRecipe = async (stars) => {
    try {
      await axios.post(`${API}/ratings`, {
        session_id: sessionId,
        recipe_id: id,
        stars: stars,
        made_again: false
      });
      setRating(stars);
      toast.success(`Bedømt med ${stars} stjerner!`);
      fetchRecipe();
    } catch (error) {
      console.error('Error rating recipe:', error);
      toast.error('Kunne ikke bedømme');
    }
  };

  const addMissingToShoppingList = async (ingredients) => {
    try {
      let addedCount = 0;
      for (const ingredient of ingredients) {
        if (ingredient.role === 'required') {
          const categoryKey = ingredient.category_key && ingredient.category_key.trim() !== '' 
            ? ingredient.category_key 
            : ingredient.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-æøå]/g, '');
          
          console.log('[Add to List] Posting ingredient:', ingredient.name, 'with sessionId:', sessionId);
          const response = await axios.post(`${API}/shopping-list`, {
            session_id: sessionId,
            ingredient_name: ingredient.name,
            category_key: categoryKey,
            quantity: ingredient.quantity,
            unit: ingredient.unit,
            linked_recipe_id: id,
            linked_recipe_name: recipe.name
          });
          console.log('[Add to List] Response:', response.status, response.data);
          addedCount++;
        }
      }
      console.log('[Add to List] Successfully added', addedCount, 'items');
      toast.success(t('recipeDetail.addedToShoppingList', `Tilføjet {{count}} ingredienser til indkøbsliste!`, { count: addedCount }));
    } catch (error) {
      console.error('[Add to List] Error:', error);
      console.error('[Add to List] Error response:', error.response?.data);
      console.error('[Add to List] Error status:', error.response?.status);
      toast.error('Kunne ikke tilføje til indkøbsliste: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleHalfPortion = (checked) => {
    setUseHalfPortion(checked);
    setTargetVolumeManuallyEdited(true);
    if (checked) {
      // Halvér maskine volumen - auto-scale useEffect vil automatisk skalere
      const halfVolume = Math.round(fullMachineVolume / 2);
      setTargetVolume(halfVolume);
      toast.success('Skalerer til halv portion...');
    } else {
      // Gendan fuld maskine volumen - auto-scale useEffect vil automatisk skalere
      setTargetVolume(fullMachineVolume);
      toast.success('Skalerer til fuld portion...');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="empty-state">
        <h3 className="text-xl font-bold mb-2">Opskrift ikke fundet</h3>
        <Button onClick={() => navigate('/recipes')}>Tilbage til Opskrifter</Button>
      </div>
    );
  }

  const ingredientsToShow = scaledData ? scaledData.scaled_ingredients : (recipe.ingredients || []);

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-in" data-testid="recipe-detail-page">
      {/* Welcome access banner — shown only when a guest opens a Pro recipe
          via their onboarding interest. Disappears on logout / new session. */}
      {recipe && !recipe.is_free && (!hasPremiumAccess(user)) && hasWelcomeAccessTo(recipe.type) && (
        <div
          className="rounded-xl border-l-4 border-orange-400 bg-gradient-to-r from-yellow-50 to-orange-50 px-4 py-3 flex items-start gap-3 shadow-sm"
          data-testid="welcome-access-banner"
        >
          <span className="text-2xl leading-none">🎁</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-orange-900">
              Velkomstadgang — prøv denne opskrift nu
            </p>
            <p className="text-xs text-orange-800 mt-0.5">
              Du har midlertidig adgang til betalte opskrifter i dine valgte kategorier i denne session.
              Vælg Light eller Pro for at beholde adgangen.
            </p>
          </div>
          <Link
            to="/pricing"
            onClick={() =>
              trackEvent(ANALYTICS_EVENTS.WELCOME_ACCESS_UPGRADE_CLICKED, {
                recipe_id: recipe?.id,
                recipe_type: recipe?.type,
                source: 'recipe_detail_banner',
              })
            }
            className="hidden sm:inline-flex items-center text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg whitespace-nowrap"
            data-testid="welcome-access-upgrade-link"
          >
            Se abonnementer
          </Link>
        </div>
      )}

      {/* Back Button & Admin Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold"
        >
          <FaArrowLeft /> {t('recipeDetail.back')}
        </button>

        {/* Admin Navigation Buttons */}
        {isAdmin() && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                console.log(`[Admin Nav] Forrige clicked, prevRecipeId: ${prevRecipeId}`);
                if (prevRecipeId) navigate(`/recipes/${prevRecipeId}`);
              }}
              disabled={!prevRecipeId}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                prevRecipeId 
                  ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              ← {t('recipeDetail.previous')}
            </button>
            <span className="text-sm text-gray-500">
              {allRecipes.length > 0 && recipe && 
                `${allRecipes.findIndex(r => r.id === id) + 1} / ${allRecipes.length}`
              }
            </span>
            <button
              onClick={() => {
                console.log(`[Admin Nav] Næste clicked, nextRecipeId: ${nextRecipeId}`);
                if (nextRecipeId) navigate(`/recipes/${nextRecipeId}`);
              }}
              disabled={!nextRecipeId}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                nextRecipeId 
                  ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('recipeDetail.next')} →
            </button>
          </div>
        )}
      </div>

      {/* Header - Two Column Layout */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Left: Description & Info */}
          <div className="p-6 md:p-8 flex flex-col justify-center order-2 md:order-1 min-w-0">
            {/* Type Icon - Desktop (above description) */}
            {recipe.type && (
              <div className="hidden md:flex items-center gap-3 mb-6">
                <img 
                  src={`/icons/${recipe.type}.png`} 
                  alt={getCategoryDisplayName(recipe.type)}
                  className="w-16 h-16 rounded-full border-2 border-gray-200 shadow-md bg-white p-2"
                />
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Type</p>
                  <p className="text-lg font-bold text-gray-800">{getCategoryDisplayName(recipe.type)}</p>
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <h1 className="text-3xl md:text-4xl font-bold mb-3">{toSentenceCase(recipe.name)}</h1>

              {/* Kvalitetsstempel — vises kun for offentligt publicerede
                  opskrifter der er blevet godkendt af redaktionen.
                  Bruger `approval_status === 'approved'` OR `author === 'system'`
                  (system-opskrifter er implicit kvalitetssikrede). */}
              {recipe.is_published && (recipe.approval_status === 'approved' || recipe.author === 'system') && (
                <div
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 bg-emerald-50 border border-emerald-300 px-3 py-1 rounded-full mb-3"
                  data-testid="recipe-quality-badge"
                  title="Denne opskrift er kvalitetssikret af SlushBooks redaktion."
                >
                  <FaCheckCircle className="text-emerald-600" />
                  <span>Kvalitetssikret af SlushBook</span>
                </div>
              )}

              {/* Familie — optional, finer-grained label inside the Type. */}
              {recipe.family && (
                <div
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full mb-3"
                  data-testid="recipe-detail-family"
                >
                  <span aria-hidden>🧬</span>
                  <span>Familie: <strong>{recipe.family}</strong></span>
                </div>
              )}
              
              {/* Author Info - User-created recipes */}
              {recipe.author && recipe.author !== 'system' && recipe.author_name && (
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Forfatter:</span>
                  <button
                    onClick={() => navigate(`/recipes?author=${recipe.author}`)}
                    className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm hover:underline flex items-center gap-2"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-full text-xs font-bold flex items-center justify-center border-2 border-cyan-200">
                      {recipe.author_name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                    </div>
                    {recipe.author_name}
                  </button>
                </div>
              )}
              
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => window.location.href = '/brix-info'}
                  className="brix-indicator text-sm px-3 py-2 hover:scale-110 transition-transform cursor-pointer"
                  title="Klik for at lære om Brix"
                >
                  {recipe.target_brix}°Bx
                </button>
                {recipe.alcohol_flag && (
                  <span className="alcohol-badge text-xs" data-testid="recipe-alcohol-badge">
                    <FaWineBottle />{' '}
                    {recipe.alcohol_percentage > 0
                      ? `${Number(recipe.alcohol_percentage).toFixed(1)}% · 18+`
                      : '18+'}
                  </span>
                )}
              </div>
            </div>
            
            <RichText
              text={recipe.description}
              className="text-gray-700 text-base md:text-lg mb-4"
            />
            
            {/* Rejection Notice - Show if recipe is rejected */}
            {recipe.approval_status === 'rejected' && (
              <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                <div className="flex items-start">
                  <FaTimes className="text-red-500 mt-1 mr-3 flex-shrink-0" size={20} />
                  <div>
                    <h4 className="text-red-800 font-semibold mb-1">Opskrift Afvist</h4>
                    <p className="text-red-700 text-sm">
                      {recipe.rejection_reason || 'Din opskrift blev afvist af administrator.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Pending Notice - Show ONLY to recipe author or admin */}
            {recipe.approval_status === 'pending' && 
             recipe.is_published === true && 
             (isAdmin() || recipe.author === sessionId) && (
              <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
                <div className="flex items-start">
                  <span className="text-yellow-500 mt-1 mr-3 flex-shrink-0 text-xl">⏳</span>
                  <div>
                    <h4 className="text-yellow-800 font-semibold mb-1">Afventer Godkendelse</h4>
                    <p className="text-yellow-700 text-sm">
                      {isAdmin() 
                        ? 'Denne opskrift afventer godkendelse.'
                        : 'Din opskrift venter på godkendelse fra administrator.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Info */}
            <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 flex-wrap">
              <span>📏 Basis: {recipe.base_volume_ml || 2700}ml</span>
              <button
                onClick={() => window.location.href = '/brix-info'}
                className="hover:text-cyan-600 hover:scale-105 transition-all cursor-pointer underline decoration-dotted font-medium inline-block bg-transparent border-0"
              >
                🍬 Sukkergrad: {recipe.target_brix}°Bx
              </button>
              {typeof recipe.recommended_freeze_celsius === 'number' &&
                !Number.isNaN(recipe.recommended_freeze_celsius) && (
                  <span data-testid="recipe-freeze-celsius">
                    ❄️ Anbefalet maskintemperatur: ca. {recipe.recommended_freeze_celsius} °C
                  </span>
                )}
            </div>
            
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {recipe.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Rating */}
            <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    data-testid={`star-${star}`}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => rateRecipe(star)}
                    className="transition-transform hover:scale-125"
                  >
                    <FaStar
                      size={20}
                      className={(
                        hoverRating >= star || rating >= star
                          ? 'text-yellow-500'
                          : 'text-gray-300'
                      )}
                    />
                  </button>
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {recipe.rating_count} bedømmelser
              </span>
            </div>

            {/* Action Buttons — flex-wrap sikrer at knapperne aldrig
                deborder venstre kolonne, uanset tekstlængde. `min-w-0` på
                den overordnede kolonne forhindrer grid-child-overflow. */}
            <div className="flex flex-wrap gap-2 mt-6">
              {/* Rediger - Only for recipe author */}
              {isAuthor() && (
                <Link
                  to={`/edit-recipe/${recipe.id}`}
                  data-testid="edit-recipe-button"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm font-medium">Rediger</span>
                </Link>
              )}
              
              {/* Del - Open to all users on all recipes (organic marketing) */}
              <button
                onClick={handleShare}
                data-testid="share-recipe-button"
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
              >
                <FaShare size={16} />
                <span className="text-sm font-medium">Del Opskrift</span>
              </button>
              
              {/* Slet - Only for admin and recipe author. `ml-auto` fjernet
                  fordi den skubbede knapper ud af venstre kolonne når
                  Toggle Free/Pro også var synlig (admin-view). */}
              {(isAdmin() || isAuthor()) && (
                <button
                  onClick={deleteRecipe}
                  data-testid="delete-recipe-button"
                  className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                >
                  <FaTrash size={16} />
                  <span className="text-sm font-medium">Slet Opskrift</span>
                </button>
              )}
              
              {/* Toggle Free/Pro - Only for admin */}
              {isAdmin() && (
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.patch(
                        `${API}/admin/recipes/${id}/toggle-free`,
                        {},
                        { withCredentials: true }
                      );
                      toast.success(response.data.message);
                      // Refresh recipe to show new status
                      fetchRecipe();
                    } catch (error) {
                      console.error('Error toggling free status:', error);
                      toast.error('Kunne ikke ændre status');
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    recipe.is_free 
                      ? 'bg-green-100 hover:bg-green-200 text-green-700' 
                      : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700'
                  }`}
                >
                  <span className="text-sm font-medium">
                    {recipe.is_free ? '✓ Gratis for gæster' : '🔒 Kun for medlemmer'}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Right: Image (Portrait) */}
          <div className="relative h-96 md:h-auto order-1 md:order-2">
            {recipe.image_url && recipe.image_url !== '/api/images/placeholder.jpg' ? (
              <img 
                src={recipe.image_url} 
                alt={recipe.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className={`w-full h-full bg-gradient-to-br from-${recipe.color}-500 to-${recipe.color}-600`}></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            
            {/* Favorite Heart Icon - Top Right */}
            <button
              onClick={toggleFavorite}
              data-testid="toggle-favorite-button"
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
              title={recipe.is_favorite ? 'Fjern favorit' : 'Tilføj favorit'}
            >
              {recipe.is_favorite ? (
                <FaHeart className="text-red-500" size={24} />
              ) : (
                <FaRegHeart className="text-gray-600" size={24} />
              )}
            </button>
            
            {/* Type Icon - Mobile (in corner of image) */}
            {recipe.type && (
              <div className="md:hidden absolute bottom-4 left-4">
                <img 
                  src={`/icons/${recipe.type}.png`} 
                  alt={getCategoryDisplayName(recipe.type)}
                  className="w-12 h-12 rounded-full border-2 border-white shadow-lg bg-white p-1"
                  title={getCategoryDisplayName(recipe.type)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scaling Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <FaBalanceScale /> {t('recipeDetail.scaleRecipe')}
        </h2>
        <div className="space-y-4">
          <div>
            <Label>{t('recipeDetail.targetVolume')}</Label>
            <div className="flex gap-2 mt-2">
              <input
                type="number"
                data-testid="target-volume-input"
                value={targetVolume}
                onChange={(e) => {
                  setTargetVolumeManuallyEdited(true);
                  setTargetVolume(parseInt(e.target.value));
                }}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
              />
              <Button onClick={scaleRecipe} data-testid="scale-button">
                {t('recipeDetail.scale')}
              </Button>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => { setTargetVolumeManuallyEdited(true); setTargetVolume(6000); }}
                className="px-3 py-1 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
              >
                6L
              </button>
              <button
                onClick={() => { setTargetVolumeManuallyEdited(true); setTargetVolume(12000); }}
                className="px-3 py-1 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
              >
                12L
              </button>
              <button
                onClick={() => { setTargetVolumeManuallyEdited(true); setTargetVolume(24000); }}
                className="px-3 py-1 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
              >
                2x12L
              </button>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <input
                type="checkbox"
                id="half-portion"
                checked={useHalfPortion}
                onChange={(e) => toggleHalfPortion(e.target.checked)}
                className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
              />
              <label htmlFor="half-portion" className="text-sm font-medium text-gray-700">
                {t('recipeDetail.halfPortion', { volume: Math.round(fullMachineVolume / 2) })}
              </label>
            </div>
          </div>

          {scaledData && (
            <div className="bg-cyan-50 rounded-lg p-4">
              <p className="font-semibold mb-2">
                {t('recipeDetail.scaledTo', { volume: (scaledData.target_volume_ml / 1000).toFixed(2), factor: scaledData.scale_factor.toFixed(2) })}
              </p>
              <p className="text-sm text-gray-700">
                {t('recipeDetail.result', { result: scaledData.resulting_brix, target: scaledData.target_brix })}
              </p>
              {scaledData.brix_adjustment && (
                <p className="text-sm text-orange-700 mt-2">
                  ⚠️ {scaledData.brix_adjustment}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Ingredients */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{t('recipes.ingredients')}</h2>
          {user ? (
            <Button
              onClick={() => addMissingToShoppingList(recipe.ingredients)}
              data-testid="add-to-shopping-list"
              variant="outline"
            >
              <FaShoppingCart className="mr-2" /> {t('recipeDetail.addToList')}
            </Button>
          ) : (
            <Button
              onClick={() => toast.info('Log ind for at bruge indkøbsliste')}
              variant="outline"
              className="opacity-50 cursor-not-allowed"
              disabled
            >
              <FaShoppingCart className="mr-2" /> {t('recipeDetail.addToListProOnly')}
            </Button>
          )}
        </div>
        <div className="space-y-3">
          {ingredientsToShow.map((ingredient, index) => {
            const mappingId = getMappingForIngredient(ingredient.name);
            
            return (
              <div
                key={index}
                data-testid={`ingredient-${index}`}
                className="p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <span className="font-medium">{getLocalizedIngredientName(ingredient)}</span>
                    {ingredient.role === 'optional' && (
                      <span className="ml-2 text-xs text-gray-500">({t('recipeDetail.optional')})</span>
                    )}
                  </div>
                  <span className="font-semibold text-cyan-600">
                    {ingredient.quantity} {ingredient.unit}
                  </span>
                </div>
                {/* Show redirect links for ALL users */}
                {mappingId && (
                  <a
                    href={`${REDIRECT_API}/${mappingId}?country=${userCountry}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors"
                  >
                    <FaShoppingCart className="w-4 h-4" />
                    <span>{t('recipeDetail.shop')}</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Admin/Editor: Match-Token Debug Panel */}
      {(isAdmin() || user?.role === 'editor') && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-2xl p-5"
          data-testid="admin-match-index-panel"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                🔧 Match-Index <span className="text-xs font-normal text-amber-700">(admin debug)</span>
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                Disse tokens er hvad Match Finder ser for denne opskrift. Stopord (vand, is, …) er auto-udeladt.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setMatchIndexOpen(!matchIndexOpen); if (!matchIndex) fetchMatchIndex(); }}
                disabled={matchIndexLoading}
                data-testid="admin-toggle-match-index"
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
              >
                {matchIndexOpen ? 'Skjul' : 'Vis index'}
              </button>
              <button
                onClick={reindexRecipe}
                disabled={matchIndexLoading}
                data-testid="admin-reindex-recipe"
                className="px-4 py-2 bg-white hover:bg-amber-100 border border-amber-300 text-amber-900 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
              >
                {matchIndexLoading ? 'Henter…' : 'Genberegn'}
              </button>
            </div>
          </div>

          {matchIndexOpen && matchIndex && (
            <div className="mt-4 space-y-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-1">
                  Flat token-liste ({(matchIndex.match_tokens || []).length})
                </div>
                {(matchIndex.match_tokens || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {matchIndex.match_tokens.map((tok, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-white border border-amber-300 text-amber-900 rounded text-xs font-mono"
                      >
                        {tok}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-amber-700 italic">Ingen tokens (måske alle ingredienser er stopord).</p>
                )}
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
                  Per ingrediens
                </div>
                <div className="bg-white rounded-lg border border-amber-200 divide-y divide-amber-100">
                  {(matchIndex.ingredients || []).map((ing, i) => (
                    <div key={i} className="p-3 text-sm" data-testid={`admin-match-index-ingredient-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{ing.name}</span>
                        <span className="text-xs text-gray-500">({ing.role})</span>
                      </div>
                      {(ing.match_tokens || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {ing.match_tokens.map((tok, j) => (
                            <span key={j} className="px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded text-xs font-mono">
                              {tok}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 italic">— ignoreret (stopord / garnish)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <details className="text-xs text-amber-800">
                <summary className="cursor-pointer hover:text-amber-900">Vis stopwords ({matchIndex.stopwords?.length || 0})</summary>
                <div className="mt-1 font-mono">{(matchIndex.stopwords || []).join(', ')}</div>
              </details>

              {matchIndex.match_tokens_updated_at && (
                <p className="text-xs text-amber-700">
                  Sidst opdateret: {new Date(matchIndex.match_tokens_updated_at).toLocaleString('da-DK')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Steps */}
      {recipe.steps && recipe.steps.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-bold mb-4">{t('recipes.instructions')}</h2>
          <ol className="space-y-3">
            {recipe.steps.map((step, index) => (
              <li key={index} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold">
                  {index + 1}
                </span>
                <div className="text-gray-700 pt-1 flex-1">
                  <RichText text={step} />
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Comments Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div 
          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 -mx-6 px-6 py-3 rounded-xl transition-colors mb-4"
          onClick={() => setCommentsCollapsed(!commentsCollapsed)}
        >
          <h2 className="text-2xl font-bold">💬 {t('recipes.comments')} ({comments.length})</h2>
          {commentsCollapsed ? (
            <FaChevronDown className="text-gray-400" size={20} />
          ) : (
            <FaChevronUp className="text-gray-400" size={20} />
          )}
        </div>
        
        {!commentsCollapsed && (
        <>
        {/* Add Comment - Pro users only */}
        {hasPremiumAccess(user) ? (
          <div className="mb-6">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Skriv en kommentar..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              rows="3"
            />
            <button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              className="mt-2 px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Send kommentar
            </button>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
            <p className="text-gray-600 text-sm">
              ✨ <strong>Kun medlemmer (Light + Pro) kan kommentere.</strong> Opgradér for at deltage i diskussionerne!
            </p>
          </div>
        )}

        {/* Comments List */}
        {comments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Ingen kommentarer endnu. Vær den første til at kommentere!</p>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                {/* Comment Header */}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{comment.user_name}</span>
                      {comment.language && (
                        <span className="text-lg" title={comment.language}>
                          {getLanguageFlag(comment.language)}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-500 text-sm ml-2">
                      {new Date(comment.created_at).toLocaleDateString('da-DK', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    {comment.updated_at && (
                      <span className="text-gray-400 text-xs ml-2">(redigeret)</span>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  {user && (user.id === comment.user_id || isAdmin()) && (
                    <div className="flex gap-2">
                      {user.id === comment.user_id && (
                        <button
                          onClick={() => {
                            setEditingCommentId(comment.id);
                            setEditCommentText(comment.comment);
                          }}
                          className="text-blue-500 hover:text-blue-700 text-sm"
                        >
                          Rediger
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Slet
                      </button>
                    </div>
                  )}
                </div>

                {/* Comment Body */}
                {editingCommentId === comment.id ? (
                  <div className="mt-2">
                    <textarea
                      value={editCommentText}
                      onChange={(e) => setEditCommentText(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                      rows="3"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleEditComment(comment.id)}
                        className="px-4 py-1.5 bg-cyan-500 text-white text-sm rounded-lg hover:bg-cyan-600"
                      >
                        Gem
                      </button>
                      <button
                        onClick={() => {
                          setEditingCommentId(null);
                          setEditCommentText('');
                        }}
                        className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                      >
                        Annuller
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                )}

                {/* Like Button */}
                {hasPremiumAccess(user) && (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={() => handleToggleLike(comment.id)}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-colors ${
                        comment.liked_by?.includes(user?.id)
                          ? 'bg-cyan-100 text-cyan-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FaHeart className={comment.liked_by?.includes(user?.id) ? 'text-cyan-500' : ''} />
                      <span>{comment.likes || 0}</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
      
      {/* Share Modal — fallback when navigator.share() is unavailable */}
      {showShareModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          data-testid="share-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowShareModal(false);
              setLinkCopied(false);
            }
          }}
        >
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl my-8">
            {/* Header: logo + close */}
            <div className="flex justify-between items-start mb-4">
              <img
                src="/slushbook-nav-logo.png"
                alt="SlushBook"
                className="h-12 w-auto"
              />
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setLinkCopied(false);
                }}
                data-testid="share-modal-close"
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Luk"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="text-center mb-5">
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Del opskriften</h3>
              <p className="text-sm text-gray-600">
                Del denne opskrift med venner og familie. Modtagere kan åbne den uden at logge ind.
              </p>
            </div>

            {/* Link box with chain icon */}
            <div className="mb-5">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2 mb-2">
                <FaLink className="text-gray-400 flex-shrink-0" size={14} />
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  data-testid="share-link-input"
                  className="flex-1 bg-transparent text-sm text-gray-700 outline-none truncate"
                />
              </div>
              <button
                onClick={copyShareLink}
                data-testid="share-copy-link"
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors border ${
                  linkCopied
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {linkCopied ? (
                  <><FaCheck size={14} /> Kopieret!</>
                ) : (
                  <><FaCopy size={14} /> Kopier link</>
                )}
              </button>
            </div>

            {/* "Del via" social icons */}
            <div className="mb-5">
              <h4 className="text-sm font-bold text-gray-800 mb-3">Del via</h4>
              {(() => {
                const urls = buildSocialShareUrls();
                const supportsNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
                const Icon = ({ icon: Ic, bg, testid, href, target, onClick, label }) => {
                  const inner = (
                    <>
                      <div className={`w-14 h-14 rounded-full ${bg} flex items-center justify-center text-white shadow-sm hover:scale-105 active:scale-95 transition-transform`}>
                        <Ic size={22} />
                      </div>
                      <span className="text-xs text-gray-700 mt-1.5">{label}</span>
                    </>
                  );
                  if (href) {
                    return (
                      <a href={href} target={target} rel="noopener noreferrer" data-testid={testid} className="flex flex-col items-center">{inner}</a>
                    );
                  }
                  return (
                    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center">{inner}</button>
                  );
                };
                return (
                  <div className="grid grid-cols-4 gap-3">
                    <Icon icon={FaFacebookF}        bg="bg-[#1877F2]" testid="share-facebook"  href={urls.facebook}  target="_blank" label="Facebook" />
                    <Icon icon={FaWhatsapp}         bg="bg-[#25D366]" testid="share-whatsapp"  href={urls.whatsapp}  target="_blank" label="WhatsApp" />
                    <Icon icon={FaXTwitter}         bg="bg-black"     testid="share-twitter"   href={urls.twitter}   target="_blank" label="X / Twitter" />
                    <Icon icon={FaEnvelope}         bg="bg-[#3B82F6]" testid="share-email"     href={urls.email}     target="_self"  label="Email" />
                    <Icon icon={FaSms}              bg="bg-[#22C55E]" testid="share-sms"       href={urls.sms}       target="_self"  label="SMS" />
                    {supportsNativeShare && (
                      <Icon
                        icon={FaEllipsisH}
                        bg="bg-gray-400"
                        testid="share-more"
                        label="Flere"
                        onClick={async () => {
                          try {
                            await navigator.share({
                              title: recipe?.name ? `${recipe.name} · SlushBook` : 'Slushice-opskrift',
                              text: recipe?.description ? recipe.description.replace(/<[^>]+>/g, '').slice(0, 140) : '',
                              url: shareLink,
                            });
                            setShowShareModal(false);
                          } catch (err) {
                            if (err && err.name !== 'AbortError') console.warn('navigator.share failed:', err);
                          }
                        }}
                      />
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Reassurance bullets */}
            <div className="space-y-3 mb-5 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                  <FaShareAlt size={14} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Native deling på mobil</div>
                  <div className="text-gray-600 text-xs">Vi åbner din enheds delingsmenu automatisk.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center text-green-600 flex-shrink-0">
                  <FaLock size={14} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Offentligt link</div>
                  <div className="text-gray-600 text-xs">Alle kan se opskriften – du behøver ikke logge ind.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-pink-100 flex items-center justify-center text-pink-500 flex-shrink-0">
                  <FaHeart size={14} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Tak fordi du deler!</div>
                  <div className="text-gray-600 text-xs">Dine delinger hjælper os med at vokse 💗</div>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowShareModal(false);
                setLinkCopied(false);
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-gray-800"
            >
              Luk
            </button>
          </div>
        </div>
      )}

      {/* AI Tips & Tricks Popup */}
      <AIChatPopup
        isOpen={showTipsAIPopup}
        onClose={() => setShowTipsAIPopup(false)}
        endpoint="/ai/help"
        title="Tips & Tricks AI"
        placeholder="Spørg om smag, konsistens, variationer, udskiftninger..."
        initialMessage={recipe ? `Jeg har en opskrift: ${recipe.name}. ` : ''}
      />
    </div>
  );
};

export default RecipeDetailPage;